import { defineStore } from 'pinia'
import { ref, computed, Ref } from 'vue'

import type { Group as ServiceGroup } from 'src/models/api/Group'
import { Group, GroupID } from 'src/models/Group'
import type { Model, ModelID } from 'src/models/Model'
import { Variable } from 'src/models/Variable'
import type { Level } from 'src/models/Level'

import api from 'src/api'

import { Dayjs } from 'dayjs'
import { UserDataService } from '@services/UserDataService'

function findModelInGroups(groups: Group[], modelId: string) {
  let modelFound: Model | undefined
  let groupFound: Group | undefined
  groups.forEach((group) => {
    if (!modelFound) {
      modelFound = getModelInGroup(group, modelId)
      groupFound = modelFound && group
    }
  })
  return { model: modelFound, group: groupFound }
}

function getModelInGroup(group: Group, modelId: string) {
  return group.models.find((model) => model.id === modelId)
}

export const useMeteoVariablesStore = defineStore('meteoVariables', () => {
  const finishDownloading = ref(false)
  const variablesFetchPromise = ref<Promise<Group[]>>()
  const groups = ref<Group[]>([]) as Ref<Group[]>
  const selectedGroup = ref<Group>()
  const selectedModel = ref<Model>()
  const selectedVariable = ref<Variable>()
  const selectedLevel = ref<Level>()
  const selectedOut = ref<Dayjs>()

  const isPerfilVerticalEnabled = computed(
    () => selectedModel.value?.isRadiosondeoEnabled
  )
  const isMeteogramEnabled = computed(
    () => selectedModel.value?.isMeteogramEnabled
  )

  const isActualActiveGroup = computed(
    () => selectedGroup.value?.isActual() || false
  )

  function getAllModels(): Model[] {
    const models: Model[] = []
    groups.value.forEach((group: Group) => {
      models.push(...group.models)
    })
    return models
  }

  function getAllVariables(): Variable[] {
    const variables: Variable[] = []
    getAllModels().forEach((model) => {
      model.variables?.forEach((variable) => {
        variables.push(variable)
      })
    })
    return variables
  }

  async function getAllLevels(): Promise<Level[]> {
    const levels: Level[] = []
    getAllVariables().forEach((variable) => {
      variable.levels.forEach((level) => {
        levels.push(level)
      })
    })
    if (!levels.length) {
      await fetchVariables()
      getAllVariables().forEach((variable) => {
        variable.levels.forEach((level) => {
          levels.push(level)
        })
      })
    }
    return levels
  }

  async function findLevelById(idLevel: string): Promise<Level | undefined> {
    let allLevels = await getAllLevels()
    if (!allLevels.length) {
      await fetchVariables()
      allLevels = await getAllLevels()
    }
    return allLevels.find((x) => x.id === idLevel)
  }

  function modelById(idModel: ModelID): Model | undefined {
    const modelGroup = findModelInGroups(groups.value, idModel)
    return modelGroup.model
  }

  function getModelsByIds(idModel: ModelID[]): Model[] {
    const models: Model[] = []
    idModel.forEach((id) => {
      const model = modelById(id)
      if (model) {
        models.push(model)
      }
    })
    return models
  }

  const lastModelOut = computed(() => selectedModel.value?.outDate)

  function variablesFromModel(idModel: string): Variable[] {
    const { model } = findModelInGroups(groups.value, idModel)
    return model && model.variables
      ? model.variables?.filter((x) => x.levels[0].path)
      : []
  }

  const isHistoricalOutActive = computed(() =>
    Boolean(
      lastModelOut.value &&
        selectedOut.value &&
        selectedOut.value.isValid() &&
        selectedOut.value?.isBefore(lastModelOut.value)
    )
  )

  function variableFromModel(
    idModel: ModelID,
    idVariable: string
  ): Variable | void {
    const model = modelById(idModel)
    return (
      model &&
      model.variables &&
      model.variables.find((x) => x.reference === idVariable)
    )
  }

  const mosaicGroups = computed(() => {
    const avoidGroups = [GroupID.ID_ACTUAL, GroupID.ID_30DAYS]
    return groups.value.filter(
      (x: Group) =>
        !avoidGroups.includes(x.id as GroupID) &&
        x.hasSomeModelInPersonalizedGroupSelected()
    )
  })

  async function fetchVariables(): Promise<Group[] | undefined> {
    try {
      finishDownloading.value = false
      if (variablesFetchPromise.value) {
        return variablesFetchPromise.value
      }
      variablesFetchPromise.value = api.variables
        .getNewVariables()
        .then((response) => {
          const groups = SET_VARIABLES(response.data)
          variablesFetchPromise.value = undefined
          return groups
        })
      return variablesFetchPromise.value
    } catch (err) {
      console.error(err)
    }
  }

  function SET_VARIABLES(serviceGroups: ServiceGroup[]) {
    console.log('Forecast API Data', serviceGroups)
    const newGroups: Group[] = []
    serviceGroups.forEach((serviceGroup) => {
      const group = new Group(serviceGroup)

      if (group.models.length > 0) {
        newGroups.push(group)
      }
    })
    groups.value = newGroups

    if (!finishDownloading.value) {
      if (selectedGroup.value) {
        selectedGroup.value = groups.value.find(
          (x) => x.id === selectedGroup.value!.id
        )
      } else {
        const group10Days = groups.value.find((x) => x.id === GroupID.ID_10DAYS)
        selectedGroup.value = group10Days || groups.value[0]
      }

      if (selectedModel.value) {
        selectedModel.value = selectedGroup.value!.models.find(
          (x) => x.id === selectedModel.value!.id
        )
      } else {
        if (selectedGroup.value) {
          let defaultModel = selectedGroup.value!.lastModelSelected
          const idDefaultModel = UserDataService.getDefaultModel()
          if (idDefaultModel) {
            const defaultGroupModel = findModelInGroups(
              groups.value,
              idDefaultModel
            )

            if (defaultGroupModel.group && defaultGroupModel.model) {
              selectedGroup.value = defaultGroupModel.group
              defaultModel = defaultGroupModel.model
            }
          }
          SET_SELECTED_MODEL(defaultModel as Model)
        }
      }
      if (selectedVariable.value) {
        selectedVariable.value = selectedModel.value!.variables!.find(
          (x) => x.reference === selectedVariable.value!.reference
        )
      } else {
        selectedVariable.value = selectedModel.value!.variables![0]
      }
      if (selectedLevel.value) {
        selectedLevel.value =
          selectedVariable.value!.getLevelByName(selectedLevel.value.name) ||
          selectedVariable.value!.levels[0]
      } else {
        selectedLevel.value =
          selectedVariable.value!.getLevelByName('2 m') ||
          selectedVariable.value!.levels[0]
      }
      finishDownloading.value = true
    }

    console.log('Forecast Variables: ', groups.value)
    return groups.value
  }

  function SET_SELECTED_GROUP(newSelectedGroup: Group) {
    selectedGroup.value = newSelectedGroup
  }

  function SET_FINISH_LOAD(isFinishLoading: boolean) {
    finishDownloading.value = isFinishLoading
  }

  function SET_SELECTED_MODEL(newSelectedModel: Model) {
    selectedModel.value = newSelectedModel
    selectedOut.value = newSelectedModel.outDate.utc()
    if (
      selectedGroup.value &&
      selectedGroup.value.hasModel(newSelectedModel.id)
    ) {
      selectedGroup.value.lastModelSelected = newSelectedModel
    }
  }

  function SET_SELECTED_VARIABLE(newSelectedVariable: Variable) {
    selectedVariable.value = newSelectedVariable
  }

  function SET_SELECTED_LEVEL(newSelectedLevel: Level) {
    selectedLevel.value = newSelectedLevel
  }

  return {
    finishDownloading,
    groups,
    selectedGroup,
    selectedModel,
    selectedVariable,
    selectedLevel,
    selectedOut,
    isPerfilVerticalEnabled,
    isMeteogramEnabled,
    isActualActiveGroup,
    modelById,
    lastModelOut,
    variablesFromModel,
    isHistoricalOutActive,
    variableFromModel,
    mosaicGroups,
    fetchVariables,
    SET_VARIABLES,
    SET_SELECTED_GROUP,
    SET_FINISH_LOAD,
    SET_SELECTED_MODEL: SET_SELECTED_MODEL,
    SET_SELECTED_VARIABLE,
    SET_SELECTED_LEVEL,
    findLevelById,
    getModelsByIds
  }
})
